import {
	Breakpoints,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Image,
	Layout,
	Text,
	TextAlignment,
	ThemeProvider,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { PortalContext } from '../../context/portal-context';
import { useTrackingManager } from '../../tracking/use-tracking-manager';
import { pageInPath } from '../../tracking/tracking-helper';

const LandingClubContainer = styled.section`
	position: relative;
	width: 100%;
	height: 95vh;
`;

const LandingClubBackgroundImage = styled.div`
	background-image: url('https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-portal/landing-club_mobile.jpg');

	@media screen and (min-width: 960px) {
		background-image: url('https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-portal/landing-club_desktop.jpg');
	}

	background-size: cover;
	background-position: center;
	height: 100%;
	width: 100%;
`;

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 95vh;
`;

const StyledLayout = styled.div`
	height: 90%;

	& > div {
		height: 90%;
		align-content: end;

		@media screen and (min-width: 1280px) {
			height: 88%;
		}
	}
`;

export const LandingClub: React.FC<{}> = () => {
	const { authService } = React.useContext(PortalContext);

	const trackingManager = useTrackingManager();

	useEffect(() => {
		pageInPath('login.app');
		trackingManager.trackClubVWGenericFeatureAppLoad({ contentId: 'login' }, {}, { vehicle: [] });
	}, []);

	return (
		<LandingClubContainer className="landing-club-section">
			<LandingClubBackgroundImage className="header-background-image">
				<StyledContainer>
					<Container
						padding={{
							top: ContainerPadding.dynamic0150,
							bottom: {
								[Breakpoints.default]: ContainerPadding.dynamic0200,
								[Breakpoints.b1280]: ContainerPadding.dynamic0100,
							},
						}}
					>
						<Layout
							data-testid="header__title-layout"
							appearance={{
								[Breakpoints.default]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 10 },
									{ name: '.', columns: 13 },
								],
								[Breakpoints.b560]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 7 },
									{ name: '.', columns: 16 },
								],
								[Breakpoints.b960]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 4 },
									{ name: '.', columns: 19 },
								],
								[Breakpoints.b1280]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 3 },
									{ name: '.', columns: 20 },
								],
								[Breakpoints.b1920]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 2 },
									{ name: '.', columns: 21 },
								],
							}}
							allowOverflowingContent
						>
							<Image
								alt="Club VW"
								src="https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-portal/logo-club.png"
							/>
						</Layout>
					</Container>

					<StyledLayout className="styled-layout">
						<Layout
							data-testid="header__title-layout"
							appearance={{
								[Breakpoints.default]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 21 },
									{ name: '.', columns: 2 },
									{ name: '.', columns: 1 },
									{ name: 'b', columns: 22 },
									{ name: '.', columns: 1 },
								],
								[Breakpoints.b560]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 20 },
									{ name: '.', columns: 3 },
									{ name: '.', columns: 1 },
									{ name: 'b', columns: 22 },
									{ name: '.', columns: 1 },
								],
								[Breakpoints.b960]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 14 },
									{ name: '.', columns: 9 },
									{ name: '.', columns: 1 },
									{ name: 'b', columns: 16 },
									{ name: '.', columns: 7 },
								],
								[Breakpoints.b1280]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 13 },
									{ name: '.', columns: 10 },
									{ name: '.', columns: 1 },
									{ name: 'b', columns: 13 },
									{ name: '.', columns: 10 },
								],
								[Breakpoints.b1600]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 11 },
									{ name: '.', columns: 12 },
									{ name: '.', columns: 1 },
									{ name: 'b', columns: 11 },
									{ name: '.', columns: 12 },
								],
								[Breakpoints.b1920]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 10 },
									{ name: '.', columns: 13 },
									{ name: '.', columns: 1 },
									{ name: 'b', columns: 9 },
									{ name: '.', columns: 14 },
								],
								[Breakpoints.b2560]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 9 },
									{ name: '.', columns: 14 },
									{ name: '.', columns: 1 },
									{ name: 'b', columns: 7 },
									{ name: '.', columns: 16 },
								],
							}}
							allowOverflowingContent
						>
							<Container wrap={'always'} stretchContent horizontalAlign={'center'}>
								<Container
									padding={{
										right: {
											[Breakpoints.default]: ContainerPadding.grid002,
											[Breakpoints.b560]: ContainerPadding.grid001,
											[Breakpoints.b960]: ContainerPadding.static0,
										},
										bottom: ContainerPadding.dynamic0050,
									}}
								>
									<Text
										appearance={TokenTextAppearance.headline500}
										color={TokenTextColor.inverted}
										textAlign={TextAlignment.left}
										bold
									>
										Si tienes un Volkswagen, regístrate o inicia sesión en el club
									</Text>
								</Container>

								<Container
									padding={{
										right: {
											[Breakpoints.b1920]: ContainerPadding.static400,
											[Breakpoints.b2560]: ContainerPadding.static0,
										},
									}}
								>
									<Text
										appearance={TokenTextAppearance.bigcopy100}
										color={TokenTextColor.inverted}
										textAlign={TextAlignment.left}
									>
										Formar parte del club no cuesta nada y a cambio puedes disfrutar de todo solo por ser cliente de
										Volkswagen.
									</Text>
								</Container>
							</Container>
							<Container
								wrap={{ [Breakpoints.default]: 'always', [Breakpoints.b560]: 'never' }}
								verticalAlign={'center'}
								horizontalAlign={'center'}
								padding={{
									top: ContainerPadding.dynamic0130,
									bottom: ContainerPadding.static250,
								}}
								gutter={{
									[Breakpoints.default]: ContainerGutter.static250,
									[Breakpoints.b1280]: ContainerGutter.static450,
								}}
								stretchContent
							>
								<Container stretchContent>
									<Text textAlign={TextAlignment.center}>
										<CTA
											tag="button"
											emphasis="primary"
											aria-label="vamos"
											size="large"
											onClick={() => {
												authService.login(undefined, window.location.origin + window.location.pathname);
												trackingManager.trackClubVWButtonClick(
													'Empezar',
													{ contentId: 'Start login club VW' },
													{ vehicle: [] },
												);
											}}
											stretchContent
										>
											Empezar
										</CTA>
									</Text>
								</Container>

								<Container
									shrinkContent={{ [Breakpoints.default]: false, [Breakpoints.b960]: false }}
									stretchContent={{ [Breakpoints.default]: true, [Breakpoints.b960]: true }}
								>
									<ThemeProvider theme={'inverted'}>
										<Text
											textAlign={{
												[Breakpoints.default]: TextAlignment.center,
												[Breakpoints.b560]: TextAlignment.left,
											}}
											color={TokenTextColor.inverted}
											bold
										>
											<CTA
												tag="a"
												emphasis="tertiary"
												href="https://www.volkswagen.es/es/clientes/beneficios-club-volkswagen.html#servicios"
												target="_blank"
												onClick={() => {
													trackingManager.trackClubVWButtonClick(
														'Descubre los beneficios',
														{ contentId: 'Discover benefits club VW' },
														{ vehicle: [] },
													);
												}}
											>
												Descubre los beneficios
											</CTA>
										</Text>
									</ThemeProvider>
								</Container>
							</Container>
						</Layout>
					</StyledLayout>
				</StyledContainer>
			</LandingClubBackgroundImage>
		</LandingClubContainer>
	);
};
